(() => {
  class NecklaceConfigurator extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.currentMetal = 'gold';
      this.activeCollection = 'all';
      this.activeColor = 'all';
      this.hideUnavailable = false;
      this.showFavorites = false;
      this.favorites = new Set();
      this.selectedTiles = new Map();
      this.previewTileId = null;
      this.products = [];
      this.currentNecklace = null;
      this.colorList = [];
      this.collectionHandles = [];
      this.favoriteStorageKey = 'dnc_favorites_v2';
      this.mobileFilterMedia = null;
      this.handleMobileFilterDock = null;
      this.cartExpanded = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.favoriteStorageKey = `dnc_favorites_${this.dataset.sectionId || 'v2'}`;
      this.colorList = this.parseColorList();
      this.collectionHandles = this.parseCollections(this.dataset.collectionHandles);

      this.loadFavorites();
      this.loadProducts();
      this.setupMobileFilterDock();
      this.setupEventListeners();
      this.switchMetal('gold');
      this.loadNecklaceFromButton('gold');
      this.applyFilters();
      this.annotateCollectionMemberships();
      this.syncFavorites();
      this.updateSelectedStates();
      this.updateBottomBar();
    }

    disconnectedCallback() {
      if (this.mobileFilterMedia && this.handleMobileFilterDock) {
        if (this.mobileFilterMedia.removeEventListener) {
          this.mobileFilterMedia.removeEventListener('change', this.handleMobileFilterDock);
        } else if (this.mobileFilterMedia.removeListener) {
          this.mobileFilterMedia.removeListener(this.handleMobileFilterDock);
        }
      }
    }

    parseColorList() {
      try {
        const colors = JSON.parse(this.dataset.colors || '[]');
        return Array.isArray(colors)
          ? colors.map((color) => String(color || '').trim()).filter(Boolean)
          : [];
      } catch {
        return [];
      }
    }

    loadProducts() {
      const cards = Array.from(this.querySelectorAll('[data-dnc-product-card]'));

      this.products = cards
        .map((card) => {
          const color = card.dataset.productColor || this.inferColorFromTitle(card.dataset.productTitle);
          card.dataset.productColor = color || '';

          return {
            id: Number(card.dataset.productId),
            handle: card.dataset.productHandle || '',
            title: card.dataset.productTitle || '',
            price: Number(card.dataset.productPrice) || 0,
            priceFormatted: card.dataset.productPriceFormatted || '',
            image: card.dataset.productImage || '',
            color,
            normalizedColor: this.normalizeValue(color),
            collections: this.parseCollections(card.dataset.productCollections),
            normalizedCollections: this.parseCollections(card.dataset.productCollections).map((collection) =>
              this.normalizeCollection(collection)
            ),
            configuratorImage: card.dataset.configuratorImage || card.dataset.productImage || '',
            variantId: Number(card.dataset.variantId),
            available: !this.isFalseLike(card.dataset.available),
            variantAvailable: !this.isFalseLike(card.dataset.variantAvailable),
            inventoryQuantity: Number(card.dataset.inventoryQuantity),
            inventoryManagement: card.dataset.inventoryManagement || '',
            inventoryPolicy: card.dataset.inventoryPolicy || '',
            element: card
          };
        })
        .filter((product) => Number.isFinite(product.id));

      this.products.forEach((product) => {
        product.element.classList.toggle('is-soldout', this.isSoldOut(product));

        product.element.addEventListener('click', () => {
          if (this.isSoldOut(product)) return;
          this.previewTile(product.id);
        });

        const addTile = product.element.querySelector('[data-dnc-add-tile]');
        if (addTile) {
          addTile.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (this.isSoldOut(product)) return;
            const quantityAction = event.target.closest('[data-dnc-card-quantity-action]')?.dataset.dncCardQuantityAction;
            if (quantityAction === 'decrease') {
              this.updateTileQuantity(product.id, 'decrease');
            } else {
              this.addTileToConfiguratorCart(product.id);
            }
          });
        }

        const favorite = product.element.querySelector('[data-dnc-favorite]');
        if (favorite) {
          favorite.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.toggleFavorite(product.id);
          });
        }
      });
    }

    parseCollections(value) {
      return String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }

    setupMobileFilterDock() {
      const filters = this.querySelector('.dnc-configurator__filters');
      const mobileSlot = this.querySelector('[data-dnc-mobile-filter-slot]');

      if (!filters || !mobileSlot || !window.matchMedia) return;

      const desktopParent = filters.parentNode;
      const desktopNextSibling = filters.nextSibling;

      this.mobileFilterMedia = window.matchMedia('(max-width: 989px)');
      this.handleMobileFilterDock = () => {
        if (this.mobileFilterMedia.matches) {
          if (filters.parentNode !== mobileSlot) {
            mobileSlot.appendChild(filters);
          }
          return;
        }

        if (filters.parentNode !== desktopParent) {
          desktopParent.insertBefore(filters, desktopNextSibling);
        }
      };

      this.handleMobileFilterDock();

      if (this.mobileFilterMedia.addEventListener) {
        this.mobileFilterMedia.addEventListener('change', this.handleMobileFilterDock);
      } else if (this.mobileFilterMedia.addListener) {
        this.mobileFilterMedia.addListener(this.handleMobileFilterDock);
      }
    }

    setupEventListeners() {
      this.querySelectorAll('[data-dnc-metal]').forEach((button) => {
        button.addEventListener('click', () => {
          this.switchMetal(button.dataset.dncMetal);
          this.loadNecklaceFromButton(button.dataset.dncMetal);
        });
      });

      const collectionSelect = this.querySelector('[data-dnc-collection]');
      if (collectionSelect) {
        collectionSelect.addEventListener('change', () => {
          this.activeCollection = collectionSelect.value || 'all';
          this.applyFilters();
        });
      }

      const colorSelect = this.querySelector('[data-dnc-color]');
      if (colorSelect) {
        colorSelect.addEventListener('change', () => {
          this.activeColor = colorSelect.value || 'all';
          this.applyFilters();
        });
      }

      const availabilityButton = this.querySelector('[data-dnc-availability]');
      if (availabilityButton) {
        availabilityButton.addEventListener('click', () => {
          this.hideUnavailable = !this.hideUnavailable;
          availabilityButton.classList.toggle('is-active', this.hideUnavailable);
          availabilityButton.textContent = this.hideUnavailable ? 'Show returning soon' : 'Hide returning soon';
          this.applyFilters();
        });
      }

      const favoritesButton = this.querySelector('[data-dnc-favorites]');
      if (favoritesButton) {
        favoritesButton.addEventListener('click', () => {
          this.showFavorites = !this.showFavorites;
          favoritesButton.classList.toggle('is-active', this.showFavorites);
          this.applyFilters();
        });
      }

      const necklaceButton = this.querySelector('[data-action="add-necklace"]');
      const tilesButton = this.querySelector('[data-action="add-tiles"]');
      const allButton = this.querySelector('[data-action="add-all"]');
      const cartToggle = this.querySelector('[data-dnc-cart-toggle]');

      if (necklaceButton) necklaceButton.addEventListener('click', () => this.addNecklaceToCart());
      if (tilesButton) tilesButton.addEventListener('click', () => this.addTilesToCart());
      if (allButton) allButton.addEventListener('click', () => this.addAllToCart());
      if (cartToggle) {
        cartToggle.addEventListener('click', () => {
          this.cartExpanded = !this.cartExpanded;
          this.updateBottomBar();
        });
      }
    }

    async annotateCollectionMemberships() {
      if (!this.collectionHandles.length) return;

      const productsByHandle = new Map(this.products.map((product) => [product.handle, product]));

      await Promise.all(
        this.collectionHandles.map(async (handle) => {
          try {
            const response = await fetch(`/collections/${handle}/products.json?limit=250`, {
              headers: { Accept: 'application/json' }
            });
            if (!response.ok) return;

            const data = await response.json();
            const products = Array.isArray(data?.products) ? data.products : [];

            products.forEach((productJson) => {
              const product = productsByHandle.get(productJson?.handle);
              if (!product) return;

              const normalizedHandle = this.normalizeCollection(handle);
              if (!product.normalizedCollections.includes(normalizedHandle)) {
                product.collections.push(handle);
                product.normalizedCollections.push(normalizedHandle);
                product.element.dataset.productCollections = product.collections.join(',');
              }
            });
          } catch {}
        })
      );

      this.applyFilters();
    }

    switchMetal(metal) {
      this.currentMetal = metal || 'gold';

      this.querySelectorAll('[data-dnc-metal]').forEach((button) => {
        button.classList.toggle('active', button.dataset.dncMetal === this.currentMetal);
      });

      this.querySelectorAll('[data-dnc-necklace-metal]').forEach((element) => {
        element.style.display = element.dataset.dncNecklaceMetal === this.currentMetal ? 'block' : 'none';
      });
    }

    loadNecklaceFromButton(metal) {
      const button = this.querySelector(`[data-dnc-metal="${metal}"]`);

      if (button && button.dataset.title && Number(button.dataset.variantId) > 0) {
        this.currentNecklace = {
          title: button.dataset.title,
          price: button.dataset.price,
          priceCents: Number(button.dataset.priceCents) || 0,
          variantId: Number(button.dataset.variantId),
          image: button.dataset.image || '',
          metal
        };
      } else {
        this.currentNecklace = null;
      }

      this.updateBottomBar();
    }

    applyFilters() {
      let visibleCount = 0;

      this.classList.toggle('is-showing-favorites', this.showFavorites);

      this.products.forEach((product) => {
        const matchesCollection =
          this.activeCollection === 'all' ||
          product.normalizedCollections.includes(this.normalizeCollection(this.activeCollection));
        const matchesColor =
          this.activeColor === 'all' || product.normalizedColor === this.normalizeValue(this.activeColor);
        const matchesAvailability = !this.hideUnavailable || !this.isSoldOut(product);
        const matchesFavorites = !this.showFavorites || this.favorites.has(product.id);
        const isVisible = matchesCollection && matchesColor && matchesAvailability && matchesFavorites;

        product.element.classList.toggle('is-hidden', !isVisible);
        if (isVisible) visibleCount += 1;
      });

      const emptyState = this.querySelector('[data-dnc-empty]');
      if (emptyState) {
        emptyState.hidden = visibleCount > 0;
        emptyState.textContent = this.showFavorites ? 'No favorites match your filters' : 'No tiles match your filters';
      }
    }

    toggleFavorite(productId) {
      if (this.favorites.has(productId)) {
        this.favorites.delete(productId);
      } else {
        this.favorites.add(productId);
      }

      this.saveFavorites();
      this.syncFavorites();
      this.applyFilters();
    }

    syncFavorites() {
      this.products.forEach((product) => {
        const favorite = product.element.querySelector('[data-dnc-favorite]');
        if (favorite) favorite.classList.toggle('is-active', this.favorites.has(product.id));
      });
    }

    previewTile(productId) {
      const product = this.products.find((item) => item.id === productId);
      if (!product) return;

      if (this.previewTileId === productId) {
        this.previewTileId = null;
      } else {
        this.previewTileId = productId;
      }

      this.updateTileOverlay();
      this.updateSelectedStates();
    }

    addTileToConfiguratorCart(productId) {
      const product = this.products.find((item) => item.id === productId);
      if (!product) return;

      const existing = this.selectedTiles.get(productId);
      this.selectedTiles.set(productId, {
        ...product,
        quantity: existing ? existing.quantity + 1 : 1
      });

      this.previewTileId = productId;
      this.updateTileOverlay();
      this.updateSelectedStates();
      this.updateBottomBar();
    }

    updateTileOverlay() {
      const overlay = this.querySelector('[data-dnc-tile-overlay]');
      if (!overlay) return;

      const selectedTile =
        this.products.find((product) => product.id === this.previewTileId) || Array.from(this.selectedTiles.values())[0];
      if (selectedTile && selectedTile.configuratorImage) {
        overlay.innerHTML = `<img src="${selectedTile.configuratorImage}" alt="${this.escapeHtml(selectedTile.title)}">`;
      } else {
        overlay.innerHTML = '';
      }
    }

    updateSelectedStates() {
      this.products.forEach((product) => {
        product.element.classList.toggle('is-selected', this.previewTileId === product.id);
        product.element.classList.toggle('is-previewed', this.previewTileId === product.id);

        const addTile = product.element.querySelector('[data-dnc-add-tile]');
        if (addTile) {
          const selectedTile = this.selectedTiles.get(product.id);
          const quantity = selectedTile?.quantity || 0;
          const count = addTile.querySelector('[data-dnc-add-count]');
          const minus = addTile.querySelector('[data-dnc-card-quantity-action="decrease"]');

          addTile.classList.toggle('is-active', quantity > 0);
          if (count) {
            count.hidden = quantity === 0;
            count.textContent = quantity > 0 ? String(quantity) : '';
          }
          if (minus) {
            minus.hidden = quantity === 0;
          }
        }
      });
    }

    removeItem(type, productId) {
      if (type === 'necklace') {
        this.currentNecklace = null;
      }

      if (type === 'tile') {
        this.selectedTiles.delete(productId);
        if (this.previewTileId === productId) {
          this.previewTileId = this.selectedTiles.size ? Array.from(this.selectedTiles.keys())[0] : null;
        }
        this.updateTileOverlay();
        this.updateSelectedStates();
      }

      this.updateBottomBar();
    }

    updateBottomBar() {
      const selectedItems = this.querySelector('[data-dnc-selected-items]');
      const subtotalElement = this.querySelector('[data-subtotal]');
      const bottomBar = this.querySelector('[data-dnc-bottom-bar]');
      const cartToggle = this.querySelector('[data-dnc-cart-toggle]');
      const cartCount = this.querySelector('[data-dnc-cart-count]');
      if (!selectedItems || !subtotalElement) return;

      const items = [];
      let subtotal = 0;
      let itemCount = 0;

      if (this.currentNecklace) {
        items.push({
          type: 'necklace',
          name: this.currentNecklace.title,
          priceFormatted: this.currentNecklace.price,
          image: this.currentNecklace.image
        });
        subtotal += this.currentNecklace.priceCents;
        itemCount += 1;
      }

      this.selectedTiles.forEach((product) => {
        const quantity = product.quantity || 1;
        items.push({
          type: 'tile',
          id: product.id,
          name: product.title,
          priceFormatted: product.priceFormatted,
          image: product.image,
          quantity
        });
        subtotal += product.price * quantity;
        itemCount += quantity;
      });

      selectedItems.innerHTML = items.map((item) => this.renderSelectedItem(item)).join('');

      selectedItems.querySelectorAll('[data-dnc-remove-type]').forEach((button) => {
        button.addEventListener('click', () => {
          const type = button.dataset.dncRemoveType;
          const id = button.dataset.dncRemoveId ? Number(button.dataset.dncRemoveId) : null;
          this.removeItem(type, id);
        });
      });

      selectedItems.querySelectorAll('[data-dnc-quantity-action]').forEach((button) => {
        button.addEventListener('click', () => {
          const id = button.dataset.dncQuantityId ? Number(button.dataset.dncQuantityId) : null;
          this.updateTileQuantity(id, button.dataset.dncQuantityAction);
        });
      });

      subtotalElement.textContent = this.formatMoney(subtotal);

      if (!items.length) this.cartExpanded = false;

      if (bottomBar) {
        bottomBar.classList.toggle('is-cart-open', this.cartExpanded);
        bottomBar.classList.toggle('has-items', items.length > 0);
      }

      if (cartToggle) {
        cartToggle.setAttribute('aria-expanded', String(this.cartExpanded));
        cartToggle.disabled = items.length === 0;
      }

      if (cartCount) {
        cartCount.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
      }

      const necklaceButton = this.querySelector('[data-action="add-necklace"]');
      const tilesButton = this.querySelector('[data-action="add-tiles"]');
      const allButton = this.querySelector('[data-action="add-all"]');

      if (necklaceButton) necklaceButton.disabled = !this.currentNecklace;
      if (tilesButton) tilesButton.disabled = this.selectedTiles.size === 0;
      if (allButton) allButton.disabled = !this.currentNecklace || this.selectedTiles.size === 0;
    }

    updateTileQuantity(productId, action) {
      const product = this.selectedTiles.get(productId);
      if (!product) return;

      const currentQuantity = product.quantity || 1;
      const nextQuantity = action === 'increase' ? currentQuantity + 1 : currentQuantity - 1;

      if (nextQuantity <= 0) {
        this.removeItem('tile', productId);
        return;
      }

      this.selectedTiles.set(productId, {
        ...product,
        quantity: nextQuantity
      });

      this.updateSelectedStates();
      this.updateBottomBar();
    }

    renderSelectedItem(item) {
      const removeId = item.id ? ` data-dnc-remove-id="${item.id}"` : '';
      const image = item.image
        ? `<img src="${item.image}" alt="${this.escapeHtml(item.name)}" class="dnc-selected-item__image">`
        : '';
      const quantity = item.quantity || 1;
      const quantityControl =
        item.type === 'tile'
          ? `
            <div class="dnc-selected-item__quantity-control" aria-label="Quantity for ${this.escapeHtml(item.name)}">
              <button type="button" data-dnc-quantity-action="decrease" data-dnc-quantity-id="${item.id}" aria-label="Decrease ${this.escapeHtml(item.name)} quantity">−</button>
              <span>${quantity}</span>
              <button type="button" data-dnc-quantity-action="increase" data-dnc-quantity-id="${item.id}" aria-label="Increase ${this.escapeHtml(item.name)} quantity">+</button>
            </div>
          `
          : '';

      return `
        <div class="dnc-selected-item">
          <button class="dnc-selected-item__remove" data-dnc-remove-type="${item.type}"${removeId} aria-label="Remove ${this.escapeHtml(item.name)}">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          ${image}
          <div class="dnc-selected-item__info">
            <div class="dnc-selected-item__name">${this.escapeHtml(item.name)}</div>
            <div class="dnc-selected-item__price">${this.escapeHtml(item.priceFormatted)}</div>
            ${quantityControl}
          </div>
        </div>
      `;
    }

    makeGroupId() {
      return `dnc_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }

    buildNecklaceItem(groupId) {
      if (!this.currentNecklace || !Number.isFinite(this.currentNecklace.variantId)) return null;

      return {
        id: this.currentNecklace.variantId,
        quantity: 1,
        properties: {
          _dnc_group: groupId,
          _dnc_role: 'necklace',
          _dnc_metal: this.currentNecklace.metal || this.currentMetal
        }
      };
    }

    buildTileItems(groupId) {
      return Array.from(this.selectedTiles.values())
        .filter((product) => Number.isFinite(product.variantId))
        .map((product, index) => ({
          id: product.variantId,
          quantity: product.quantity || 1,
          properties: {
            _dnc_group: groupId,
            _dnc_role: 'tile',
            _dnc_slot: String(index + 1),
            _dnc_title: product.title || ''
          }
        }));
    }

    async addNecklaceToCart() {
      const groupId = this.makeGroupId();
      const necklace = this.buildNecklaceItem(groupId);
      if (!necklace) return;
      await this.addToCart([necklace], 'add-necklace');
    }

    async addTilesToCart() {
      const groupId = this.makeGroupId();
      const items = this.buildTileItems(groupId);
      if (!items.length) return;
      await this.addToCart(items, 'add-tiles');
    }

    async addAllToCart() {
      const groupId = this.makeGroupId();
      const necklace = this.buildNecklaceItem(groupId);
      const tiles = this.buildTileItems(groupId);
      const items = [necklace, ...tiles].filter(Boolean);
      if (!items.length) return;
      await this.addToCart(items, 'add-all');
    }

    async addToCart(items, action) {
      const button = this.querySelector(`[data-action="${action}"]`);
      const originalText = button ? button.textContent : '';

      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Adding...';
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
          const message = data?.description || data?.message || `Shopify error (${response.status})`;
          throw new Error(message);
        }

        this.openCartDrawer();
        if (button) button.textContent = 'Added';
      } catch (error) {
        console.error('Error adding to cart:', error);
        if (button) button.textContent = 'Try again';
      } finally {
        window.setTimeout(() => {
          if (button) {
            button.textContent = originalText;
            button.removeAttribute('aria-busy');
          }
          this.updateBottomBar();
        }, 900);
      }
    }

    openCartDrawer() {
      const refreshEvent = new CustomEvent('cart:refresh', { bubbles: true });
      const openEvent = new CustomEvent('cart:open', { bubbles: true });

      document.dispatchEvent(refreshEvent);
      document.documentElement.dispatchEvent(refreshEvent);
      document.dispatchEvent(openEvent);
      document.documentElement.dispatchEvent(openEvent);

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
    }

    isSoldOut(product) {
      if (!product.available || !product.variantAvailable) return true;
      return (
        Boolean(product.inventoryManagement) &&
        product.inventoryPolicy !== 'continue' &&
        Number.isFinite(product.inventoryQuantity) &&
        product.inventoryQuantity <= 0
      );
    }

    isFalseLike(value) {
      return value === false || value === 0 || String(value).toLowerCase() === 'false';
    }

    loadFavorites() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.favoriteStorageKey) || '[]');
        if (Array.isArray(stored)) {
          this.favorites = new Set(stored.map(Number).filter((id) => Number.isFinite(id)));
        }
      } catch {
        this.favorites = new Set();
      }
    }

    saveFavorites() {
      try {
        localStorage.setItem(this.favoriteStorageKey, JSON.stringify(Array.from(this.favorites)));
      } catch {}
    }

    inferColorFromTitle(title) {
      const normalizedTitle = this.normalizeValue(title);
      return (
        this.colorList
          .map((color) => String(color || '').trim())
          .filter(Boolean)
          .find((color) => normalizedTitle.includes(this.normalizeValue(color))) || ''
      );
    }

    normalizeValue(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
    }

    normalizeCollection(value) {
      return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/&/g, 'and')
        .replace(/@/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    formatMoney(cents) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  if (!customElements.get('necklace-configurator')) {
    customElements.define('necklace-configurator', NecklaceConfigurator);
  }
})();